import React, { useState } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../FirebaseConfig'; 
import { useAuthState } from 'react-firebase-hooks/auth';
import { useDocumentData } from 'react-firebase-hooks/firestore';

const CLOUDINARY_CLOUD_NAME = 'dm8uirujp'; 
const CLOUDINARY_UPLOAD_PRESET = 'unsigned_ibuyunn'; 

const Verify = () => {
  const [governmentId, setGovernmentId] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Get current user and their profile data using react-firebase-hooks
  const [user, authLoading] = useAuthState(getAuth());
  const [profile, profileLoading] = useDocumentData(user && doc(db, 'profiles', user.uid));
  
  // FIXED: Check for both 'pending' and 'approved' explicitly
  const isPending = profile?.verification_status === 'pending';
  const isApproved = profile?.verification_status === 'approved';
  
  // New users will have verification_status === null or undefined
  // Only show form if they haven't started verification yet
  const shouldShowForm = !isPending && !isApproved;
  
  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

    try {
      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        { method: 'POST', body: formData }
      );
      if (!res.ok) throw new Error('Cloudinary upload failed.');
      const data = await res.json();
      return data.secure_url;
    } catch (err) {
      console.error('Cloudinary Upload Error:', err);
      throw err;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isPending || isApproved) {
        setMessage('You cannot submit a new form at this time.');
        return;
    }
    
    // VALIDATION 1: Check files exist in state
    if (!governmentId || !selfie) {
      setMessage('Please upload both files to proceed.');
      return;
    }
    
    // VALIDATION 2: Verify they are actual File objects
    if (!(governmentId instanceof File) || !(selfie instanceof File)) {
      setMessage('Invalid file format. Please select image files.');
      return;
    }
    
    // VALIDATION 3: Check file sizes (prevent huge uploads)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (governmentId.size > MAX_FILE_SIZE || selfie.size > MAX_FILE_SIZE) {
      setMessage('Files must be smaller than 10MB.');
      return;
    }
    
    setLoading(true);
    setMessage('');
    
    try {
      // Upload files to Cloudinary with explicit error handling
      let idCardUrl;
      let selfieUrl;
      
      try {
        idCardUrl = await uploadToCloudinary(governmentId);
        // VALIDATION 4: Verify upload returned a valid URL
        if (!idCardUrl || typeof idCardUrl !== 'string' || !idCardUrl.startsWith('https://')) {
          throw new Error('ID card upload returned invalid URL');
        }
      } catch (uploadErr) {
        console.error('ID card upload failed:', uploadErr);
        setMessage('Failed to upload ID card. Please try again.');
        setLoading(false);
        return;
      }
      
      try {
        selfieUrl = await uploadToCloudinary(selfie);
        // VALIDATION 5: Verify selfie upload returned a valid URL
        if (!selfieUrl || typeof selfieUrl !== 'string' || !selfieUrl.startsWith('https://')) {
          throw new Error('Selfie upload returned invalid URL');
        }
      } catch (uploadErr) {
        console.error('Selfie upload failed:', uploadErr);
        setMessage('Failed to upload selfie. Please try again.');
        setLoading(false);
        return;
      }

      if (!user) throw new Error('User not authenticated.');

      // VALIDATION 6: Final sanity check before database write
      if (!idCardUrl || !selfieUrl) {
        throw new Error('One or both URLs are missing before database write');
      }

      // Only NOW set verification status to 'pending' after BOTH files uploaded successfully
      await setDoc(
        doc(db, 'profiles', user.uid),
        {
          idCardUrl: idCardUrl,
          selfieUrl: selfieUrl,
          verification_status: 'pending',
          verification_submitted_at: new Date(),
        },
        { merge: true }
      );

      setMessage('Verification documents submitted successfully! Your status is now pending.');
      setGovernmentId(null);
      setSelfie(null);

    } catch (err) {
      console.error(err);
      setMessage('An error occurred during submission. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || profileLoading) {
    return <p className="text-center mt-10 text-gray-500">Loading...</p>;
  }

  // Conditional rendering based on verification status
  const renderContent = () => {
    if (isApproved) {
        return (
            <div className="text-center p-6 bg-green-50 rounded-xl shadow-lg">
                <h2 className="text-2xl font-bold text-green-700">You are Verified! 🎉</h2>
                <p className="mt-2 text-green-600">Your account has been successfully verified.</p>
            </div>
        );
    }
    
    if (isPending) {
        return (
            <div className="text-center p-6 bg-yellow-50 rounded-xl shadow-lg">
                <h2 className="text-2xl font-bold text-yellow-700">Verification Pending</h2>
                <p className="mt-2 text-yellow-600">Your documents are under review. Please check back later.</p>
            </div>
        );
    }
    
    // Default case: display the form for new users who haven't uploaded documents
    if (shouldShowForm) {
        return (
            <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-xl shadow-lg">
                <h2 className="text-2xl font-bold text-center text-gray-800">Seller Verification</h2>
                <p className="text-sm text-center text-gray-600">
                    Please upload your documents to verify your account and build trust with customers.
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="governmentId" className="block text-sm font-medium text-gray-700">
                            Upload NIN / Student ID
                        </label>
                        <input
                            type="file"
                            id="governmentId"
                            accept="image/*"
                            onChange={(e) => setGovernmentId(e.target.files[0])}
                            className="mt-1 block w-full text-sm text-gray-500
                                file:mr-4 file:py-2 file:px-4 file:rounded-full
                                file:border-0 file:text-sm file:font-semibold
                                file:bg-blue-50 file:text-blue-700
                                hover:file:bg-blue-100"
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="selfie" className="block text-sm font-medium text-gray-700">
                            Upload a selfie holding your ID
                        </label>
                        <input
                            type="file"
                            id="selfie"
                            accept="image/*"
                            onChange={(e) => setSelfie(e.target.files[0])}
                            className="mt-1 block w-full text-sm text-gray-500
                                file:mr-4 file:py-2 file:px-4 file:rounded-full
                                file:border-0 file:text-sm file:font-semibold
                                file:bg-blue-50 file:text-blue-700
                                hover:file:bg-blue-100"
                            required
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full py-2 px-4 rounded-md text-white font-semibold transition-colors
                            ${loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                        {loading ? 'Submitting...' : 'Submit for Verification'}
                    </button>
                </form>

                {message && (
                    <p className={`text-center text-sm font-medium ${message.includes('success') ? 'text-green-600' : 'text-red-600'}`}>
                        {message}
                    </p>
                )}
            </div>
        );
    }
    
    // Fallback (should not reach here)
    return <p className="text-center text-gray-600">Loading verification status...</p>;
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-transparent p-4">
      {renderContent()}
    </div>
  );
};

export default Verify;